import React, { useCallback, useMemo, useState } from 'react';
import { useAccessControl } from '../../contexts/AccessControlContext';
import { StockCategoryFilter, getCategoryConfigs } from '../../types/stockCategories';
import { AccessCheckResult } from '../../constants/accessControl';
import { UpgradePromptModal } from '../../components/ui/modals/UpgradePromptModal';

interface CategoryLockOptions {
  title?: string;
  message?: string;
  featureName?: string;
}

interface LockedCategoryState extends CategoryLockOptions {
  category: StockCategoryFilter;
  accessCheck: AccessCheckResult;
}

/**
 * Shared helper for gating premium categories from UI entry points.
 * Provides simple helpers to check lock state and trigger the upgrade modal
 * without duplicating entitlement logic in every component.
 */
export function useCategoryLocking() {
  const { canAccessCategory } = useAccessControl();
  const [lockedCategory, setLockedCategory] = useState<LockedCategoryState | null>(null);

  // Cache configs so label/icon lookups are stable across renders
  const categoryConfigs = useMemo(() => getCategoryConfigs(), []);

  const isCategoryLocked = useCallback((category: StockCategoryFilter) => {
    return !canAccessCategory(category).allowed;
  }, [canAccessCategory]);

  const requireCategoryAccess = useCallback((category: StockCategoryFilter, options?: CategoryLockOptions) => {
    const accessCheck = canAccessCategory(category);

    if (accessCheck.allowed) {
      return true;
    }

    setLockedCategory({
      category,
      accessCheck,
      title: options?.title,
      message: options?.message,
      featureName: options?.featureName
    });

    return false;
  }, [canAccessCategory]);

  const handleLockedCategoryClick = useCallback((category: StockCategoryFilter, options?: CategoryLockOptions) => {
    const hasAccess = requireCategoryAccess(category, options);
    return !hasAccess;
  }, [requireCategoryAccess]);

  const closeModal = useCallback(() => setLockedCategory(null), []);

  const CategoryLockModal = lockedCategory ? (
    <UpgradePromptModal
      isOpen={true}
      onClose={closeModal}
      title={lockedCategory.title}
      message={
        lockedCategory.message
        || lockedCategory.accessCheck.upgradeMessage
        || `Upgrade to Premium to unlock ${categoryConfigs[lockedCategory.category]?.label ?? lockedCategory.category}`
      }
      requiresAuth={lockedCategory.accessCheck.requiresAuth}
      requiresPremium={lockedCategory.accessCheck.requiresPremium}
      feature={lockedCategory.featureName || categoryConfigs[lockedCategory.category]?.label || lockedCategory.category}
    />
  ) : null;

  return {
    isCategoryLocked,
    handleLockedCategoryClick,
    requireCategoryAccess,
    CategoryLockModal
  };
}

export type UseCategoryLockingReturn = ReturnType<typeof useCategoryLocking>;
